/** Espelho de `IRhAbasResposta` do backend (use-cases/rh-abas). Só o que a tela lê. */
export type EstadoAbas = "OK" | "SEM_APURACAO" | "DESLIGADO";
export type Mao = "RH" | "CORRETORA_ENVIA" | "CORRETORA_COBRA" | "CALENDARIO";
export type Direcao = "INCLUSAO" | "EXCLUSAO" | "ALTERACAO" | "TRANSFERENCIA";

export interface IItem {
  rotulo: string;
  n: number;
}

export interface IProdutoAtivos {
  produto: string;
  cobertura: number | null;
  matriz: {
    folhaComPlano: number;
    folhaSemPlano: number;
    foraComPlano: { n: number; itens: IItem[] };
    zonaCinza: number;
    saidaConcluida: { n: number; noMes: number };
  };
  porqueSemPlano: { rotulo: string; n: number; itens: IItem[] }[];
  operadoras: IItem[];
  dependentesComPlano: number;
  investimento: {
    titularesComValor: number;
    titularesComPlano: number;
    media: number;
    mediana: number;
    p90: number;
    totalMes: number;
    faixas: IItem[];
  } | null;
}

export interface ITempoParado {
  rotulo: string;
  n: number;
  medianaDias: number | null;
  p90Dias: number | null;
  acimaDe30: number;
  maisAntigaDias: number | null;
}

export interface ICompras {
  mes: {
    cobertasNoMes: number | null;
    enviadasNoMes: number;
    saidasConfirmadasNoMes: number | null;
    geramCusto: { n: number; valorMes: number | null; contratosSemFacto: number } | null;
  } | null;
  custoDeEsperar: { n: number; nComValor: number; atrasadas: number; rsMes: number | null };
  fila: { mao: Mao; direcao: Direcao; noPrazo: number; vencendo: number; atrasadas: number; calendario: number }[];
  saidasAgendadas: { data: string; n: number }[];
  tempoParado: ITempoParado[];
  eSe: { prazoDias: number; rsEvitavel: number }[] | null;
}

export interface IRhAbas {
  estado: EstadoAbas;
  diaDosNumeros: string | null;
  ativos: { titularesNaFolha: number; produtos: IProdutoAtivos[] } | null;
  compras: ICompras | null;
  avisos: string[];
}

// Espelho de contrato-interfaces.md §7 e §7.9 (v1.2) da spec o-que-vem-ai. Só o que a tela lê.
export type ClasseVisibilidade = "SO_EMPRESA" | "EMPRESA_E_STAFF";
export type SeloVisibilidadeAba = "SO_EMPRESA" | "COMPARTILHADO_COM_CORRETORA";
// Cópia de RN63_BANDS (frontend: SetupContract/Health/WorkspaceShared/Rules/planPanel.api.ts, ramo
// feat/preco-por-plano-f1) com os rótulos de bandLabel (backend: plan-price.ts). Regra C.13: quem chegar
// primeiro à main cria o módulo; o outro passa a importar no rebase.
export const FAIXAS = ["0-18", "19-23", "24-28", "29-33", "34-38", "39-43", "44-48", "49-53", "54-58", "59 ou mais"] as const;
export type Faixa = (typeof FAIXAS)[number];
export type NomeAba = "hoje" | "ativos" | "compras" | "previsto" | "gente";
export type VisibilidadeResposta = { abas: { aba: NomeAba; classe: ClasseVisibilidade; visivel: boolean; selo: SeloVisibilidadeAba | null }[] };
export type Baldes = { "0-30": number; "31-90": number; "91-180": number; "181-365": number };
export type Estimativa = { mediana: number; p25: number; p75: number; ultimos3: number; meses: number; serie: { mes: string; n: number }[] };
export type MotivoCadastro = "SEM_CONTRATO" | "SEM_CADASTRO" | "SEM_CONTA" | "DESLIGADO";
export type Cadastro = { disponivel: true; lidoEm: string } | { disponivel: false; motivo: MotivoCadastro | string }; // string: motivo novo do backend
export type FamiliaDeclarados = { "0": number; "1": number; "2": number; "3+": number; semCadastro: number; dependentes: number };
export type FamiliaEnderecaveis = { "0": number; "1": number; "2": number; "3+": number; dependentes: number };
export type Previsto = {
  vinculosAtivos: number;
  nascimentosEsperados: { "0-30": number };
  retornosDeLicenca: { maternidade: Baldes & { estimados: number }; outras: Baldes & { estimados: number; semFimInformado: number } };
  mudancasDeFaixa?: Record<keyof Baldes, Partial<Record<Faixa, number>>>;
  cadastro: Cadastro;
  titularesSemCadastro?: number;
  dependentesSemTipo?: number;
  contratosComPrazo: { experiencia: Baldes; outrosPrazos: Baldes; vencidosSemInformacao: number };
  cargaDoProximoMes: {
    admissoes: Estimativa;
    desligamentos: Estimativa & { taxa: number; porMotivo: Record<string, number>; comDireito: number | null };
  };
};
export type PrevistoResposta =
  | { disponivel: false }
  | {
      disponivel: true;
      dataCiclo: string;
      motivosQueANormaProtege: string[];
      fonteAte: string;
      cnpj: string;
      previsto: Previsto;
      dependentesPorContrato: {
        workspaceId: string;
        contrato: string;
        idade: number | null;
        porIdade: { idade: number; baldes: Baldes }[];
      }[];
    };

export type Variavel = "categoria" | "tipoDeContrato" | "sexo" | "faixaEtaria" | "tempoDeCasa" | "grauDeInstrucao" | "jornada";
export type Grupo = "maternidade" | "doencaAte15" | "doencaInss" | "acidenteTrabalho" | "outros";
export type LinhaPiramide = { titulares: number; dependentes: number; titularesQueSobem: number; dependentesQueSobem: number };
export type PiramideFaixas = Partial<Record<Faixa | "semNascimento", LinhaPiramide>>;
export type Enderecaveis = { workspaceId: string; nome: string; lidoEm: string | null; piramide?: PiramideFaixas; familia?: FamiliaEnderecaveis };
export type Gente = {
  situacao: { ativos: number; trabalhando: number; afastados: number; semFimInformado: number };
  composicao: Partial<Record<Variavel, Record<string, number>>>;
  piramide?: PiramideFaixas;
  afastados: Record<Grupo, { hoje: number; medianaDosQueVoltaram: number | null; p90DosQueVoltaram: number | null; casos: number }>;
  familia?: FamiliaDeclarados;
  cadastro: Cadastro;
  enderecaveis?: Enderecaveis[];
};
export type GenteResposta = { disponivel: false } | { disponivel: true; dataCiclo: string; fonteAte: string; cnpj: string; gente: Gente };
